// src/pages/clientes/components/FilialVincularDialog.tsx
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link as LinkIcon, Loader2, Building2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ClienteCombobox } from "@/components/shared/ClienteCombobox";

import { ClienteFilialDTO } from "@/types/cliente-filial.type";
import { ClienteDTO } from "@/types/cliente.type";
import { useFilialActions } from "@/hooks/clientes/use-filial-actions";
import { clienteService } from "@/services/api/cliente.service";

const vincularSchema = z.object({
  clienteId: z.string().min(1, "Selecione uma matriz obrigatória."),
});

type VincularFormValues = z.infer<typeof vincularSchema>;

interface FilialVincularDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filial: ClienteFilialDTO | null;
}

export function FilialVincularDialog({ open, onOpenChange, filial }: FilialVincularDialogProps) {
  const { vincularCliente, isLinking } = useFilialActions();
  const [matrizes, setMatrizes] = useState<ClienteDTO[]>([]);
  const [isLoadingMatrizes, setIsLoadingMatrizes] = useState(false);

  const methods = useForm<VincularFormValues>({
    resolver: zodResolver(vincularSchema),
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
  } = methods;

  useEffect(() => {
    if (open) {
      reset({ clienteId: "" });
      const fetchMatrizes = async () => {
        setIsLoadingMatrizes(true);
        try {
          const clientes = await clienteService.getAllActiveClientes();
          setMatrizes(clientes);
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoadingMatrizes(false);
        }
      };
      fetchMatrizes();
    }
  }, [open, reset]);

  const onSubmit = async (values: VincularFormValues) => {
    if (!filial) return;
    try {
      await vincularCliente({
        id: filial.id,
        payload: { clienteId: Number(values.clienteId) },
      });
      onOpenChange(false);
    } catch (error) {
      // Erro tratado no hook
    }
  };

  const selectOptions = matrizes
    .map((m) => ({
      label: m.nome?.trim(),
      value: m.id.toString(),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Dialog open={open} onOpenChange={(val) => !isLinking && onOpenChange(val)}>
      <DialogContent className="flex flex-col gap-0 overflow-hidden rounded-xl border-border bg-background p-0 shadow-2xl sm:max-w-[450px] sm:rounded-2xl">
        <DialogHeader className="sr-only">
          <DialogTitle>Vincular Filial</DialogTitle>
          <DialogDescription>Associe esta filial a uma matriz existente.</DialogDescription>
        </DialogHeader>

        <div className="z-20 flex shrink-0 items-center justify-between border-b bg-card px-5 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/10">
              <LinkIcon className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="mb-1 text-base font-bold leading-none tracking-tight text-foreground sm:text-lg">
                Vincular Cliente
              </h2>
              <p className="max-w-[250px] truncate text-[10px] text-muted-foreground sm:text-xs">
                Filial: {filial?.nome}
              </p>
            </div>
          </div>
        </div>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col">
            <div className="space-y-6 p-5 sm:p-6">
              <div className="space-y-2 rounded-lg border border-border/50 bg-muted/30 p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Building2 className="h-4 w-4" />
                  <span>Detalhes Importados (Publi)</span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">
                      ID Publi
                    </p>
                    <p className="truncate text-sm font-medium text-foreground">
                      {filial?.idPubli}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Grupo</p>
                    <p className="truncate text-sm font-medium text-foreground">
                      {filial?.grupo || "Não informado"}
                    </p>
                  </div>
                </div>
              </div>

              <Controller
                name="clienteId"
                control={control}
                render={({ field }) => (
                  <ClienteCombobox
                    options={selectOptions}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isLoadingMatrizes}
                  />
                )}
              />
            </div>

            <div className="flex flex-col-reverse justify-end gap-3 border-t bg-muted/10 p-5 sm:p-6 md:flex-row">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLinking}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="gap-2 bg-emerald-600 font-bold text-white hover:bg-emerald-700"
                disabled={!isValid || isLinking}
              >
                {isLinking ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Salvando...
                  </>
                ) : (
                  "Confirmar Vínculo"
                )}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
